import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Append a trailing wildcard to the last term of a search query so the native
 * /search results page prefix-matches short terms the way the predictive
 * dropdown does. Native /search only auto-prefix-matches terms of 5+ characters
 * (even with options[prefix]=last); a trailing "*" bypasses that floor. No-op
 * when the query is empty or already wildcarded.
 * @param {string} query
 * @returns {string}
 */
function withPrefixWildcard(query) {
  const trimmed = query.trim();
  if (!trimmed || trimmed.endsWith('*')) return trimmed;
  return `${trimmed}*`;
}

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();
    // Apply the prefix wildcard on submit so the results page matches the same
    // products the predictive dropdown shows for short queries. The trailing
    // "*" is stripped from the displayed value (see _search-input.liquid), so
    // re-submitting an existing search stays idempotent.
    this.refs.searchPageInput.form?.addEventListener('submit', this.#handleSubmit);
  }

  /**
   * Rewrites the query to include the prefix wildcard just before the GET
   * navigation, leaving the form's hidden type/prefix inputs intact.
   */
  #handleSubmit = () => {
    const input = this.refs.searchPageInput;
    if (input.value.trim()) input.value = withPrefixWildcard(input.value);
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
